// Reorder Console - local error boundary (unit U8; DESIGN §6.2, §6.13).
//
// WEB has no global ErrorBoundary, so one malformed engine field must not blank the whole dashboard Outlet. Wrap the
// page and each heavy panel (charts, planner sections, drawer) in <RcErrorBoundary label="…">: a render error shows
// "This panel failed to render" with Retry; the rest of the page stays usable.
//
// Props:
//   label       what failed, shown in the alert ("Weekly stock runway")
//   resetKeys   array; when any value changes the boundary resets by itself (e.g. [w, id] when the user moves on)
//   compact     smaller inline alert for panels
//   onReset     optional callback before a retry
// A failed lazy chunk (ChunkLoadError after a deploy replaced the files) cannot be retried in place - React caches the
// rejected import - so Retry reloads the page for that one case.
import React from 'react';
import { Alert, Button, Box, Typography } from '@mui/material';

const isChunkError = (e) => !!e && (e.name === 'ChunkLoadError' || /Loading (CSS )?chunk [\w-]+ failed/i.test(String(e.message || '')));

const sameKeys = (a, b) => {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) if (!Object.is(a[i], b[i])) return false;
  return true;
};

export default class RcErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, keys: props.resetKeys };
    this.retry = this.retry.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  static getDerivedStateFromProps(props, state) {
    if (state.error && !sameKeys(props.resetKeys, state.keys)) return { error: null, keys: props.resetKeys };
    if (!sameKeys(props.resetKeys, state.keys)) return { keys: props.resetKeys };
    return null;
  }

  componentDidCatch(error, info) {
    // Logged for the browser console / support; never shown raw to the user.
    console.error(`[ReorderConsole] ${this.props.label || 'panel'} failed to render`, error, info && info.componentStack);
  }

  retry() {
    const { error } = this.state;
    if (isChunkError(error)) {
      window.location.reload();
      return;
    }
    if (this.props.onReset) this.props.onReset();
    this.setState({ error: null });
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const { label, compact } = this.props;
    const chunk = isChunkError(error);
    return (
      <Box sx={{ my: compact ? 1 : 2 }}>{/* the MUI Alert below is role="alert" itself: one announcement */}
        <Alert
          severity="error"
          variant={compact ? 'standard' : 'outlined'}
          action={<Button color="inherit" size="small" onClick={this.retry}>{chunk ? 'Reload' : 'Retry'}</Button>}
        >
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {label ? `${label}: this panel failed to render` : 'This panel failed to render'}
          </Typography>
          <Typography variant="caption" component="div" sx={{ opacity: 0.85 }}>
            {chunk
              ? 'A newer version of theezdocs was deployed while this page was open. Reload to continue.'
              : 'The rest of the page still works. Retry, or reload the page if it keeps failing.'}
          </Typography>
        </Alert>
      </Box>
    );
  }
}
